import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';

/**
 * Asset types shown in home strip + assets list filter (browse menus).
 * Other API types remain available for create/edit and asset detail display.
 */
export const BROWSE_FILTER_ASSET_TYPE_NAMES = [
  'Private Office',
  'Open Space',
  'Conference Room',
  'Gym',
  'Parking Garage',
  'Podcast Studio',
  'Lecture Room',
  'Clinic',
  'Business Lounge',
  'Classroom',
  'Workshop',
] as const;

/**
 * Offizz.ai homepage / header category set (desktop banner nav).
 * Subset of {@link BROWSE_FILTER_ASSET_TYPE_NAMES} — excludes podcast, lecture, classroom, workshop.
 */
export const OFFIZZ_SITE_NAV_ASSET_TYPE_NAMES = [
  'Private Office',
  'Conference Room',
  'Open Space',
  'Gym',
  'Parking Garage',
  'Clinic',
  'Business Lounge',
] as const;

const BROWSE_FILTER_ORDER = new Map<string, number>(
  BROWSE_FILTER_ASSET_TYPE_NAMES.map((name, index) => [normalizeAssetTypeName(name), index]),
);

/**
 * Spoken synonyms (voice parser + keyword search). Regular EN/HE plurals are
 * generated from these plus the catalog labels — see {@link browseSpokenPhrasesForType}.
 */
export const BROWSE_FILTER_TYPE_EXTRA_PHRASES: Partial<
  Record<(typeof BROWSE_FILTER_ASSET_TYPE_NAMES)[number], readonly string[]>
> = {
  'Private Office': ['private offices', 'offices', 'office', 'משרדים פרטיים', 'משרדים', 'משרד'],
  'Open Space': [
    'open spaces',
    'coworking spaces',
    'coworking space',
    'coworking',
    'co-working',
    'co working',
    'אופן ספייסים',
    'מרחבים פתוחים',
    'מרחב פתוח',
  ],
  'Conference Room': [
    'conference rooms',
    'meeting rooms',
    'meeting room',
    'boardrooms',
    'boardroom',
    'board rooms',
    'board room',
    'חדרי ישיבות',
  ],
  Gym: ['gyms', 'fitness centers', 'fitness center', 'fitness', 'חדרי כושר'],
  'Parking Garage': [
    'parking garages',
    'parking lots',
    'parking lot',
    'car parks',
    'car park',
    'parking',
    'חניונים',
    'חניות',
    'חניה',
  ],
  'Podcast Studio': [
    'podcast studios',
    'podcasts',
    'podcast rooms',
    'podcast room',
    'podcast',
    'חדרי פודקאסטים',
    'חדרי פודקאסט',
    'חדר פודקאסט',
    'חדרי פודקסטים',
    'חדרי פודקסט',
    'חדר פודקסט',
    'אולפן פודקאסט',
    'אולפני פודקאסט',
    'אולפן לפודקאסט',
    'אולפן לפודקסט',
    'אולפן הקלטות',
    'סטודיו לפודקאסט',
    'סטודיו לפודקסט',
    'סטודיו פודקאסט',
    'סטודיו פודקסט',
    'סטודיו להקלטות',
    'פודקאסטים',
    'פודקאסט',
    'פודקסטים',
    'פודקסט',
  ],
  'Lecture Room': ['lecture rooms', 'lecture halls', 'lecture hall', 'auditoriums', 'auditorium', 'אולמות הרצאות'],
  Clinic: ['clinics', 'treatment rooms', 'treatment room', 'קליניקות', 'קליניקה', 'חדרי טיפול'],
  'Business Lounge': ['business lounges', 'lounges', 'lounge', 'טרקליני עסקים', 'טרקלינים', 'טרקלין'],
  Classroom: ['classrooms', 'כיתות'],
  Workshop: ['workshops', 'חדרי סדנאות', 'סדנאות', 'סדנה'],
};

/** Generic “office / משרד” — office-family browse, not a single catalog type. */
export const GENERIC_OFFICE_SEARCH_PHRASES = ['office', 'offices', 'משרד', 'משרדים'] as const;

const GENERIC_OFFICE_SEARCH_PHRASE_SET = new Set(
  GENERIC_OFFICE_SEARCH_PHRASES.map((phrase) => normalizeAssetTypeName(phrase)),
);

export function isGenericOfficeSearchPhrase(phrase: string): boolean {
  return GENERIC_OFFICE_SEARCH_PHRASE_SET.has(normalizeAssetTypeName(phrase));
}

const HEBREW_TYPE_PLURALS: Record<string, readonly string[]> = {
  'משרד פרטי': ['משרדים פרטיים'],
  משרד: ['משרדים'],
  'אופן ספייס': ['אופן ספייסים'],
  'מרחב פתוח': ['מרחבים פתוחים'],
  חניון: ['חניונים'],
  חניה: ['חניות'],
  כיתה: ['כיתות'],
  סדנה: ['סדנאות'],
  קליניקה: ['קליניקות'],
  טרקלין: ['טרקלינים'],
  'טרקלין עסקים': ['טרקליני עסקים', 'טרקלינים'],
  פודקאסט: ['פודקאסטים'],
  פודקסט: ['פודקסטים'],
};

const ENGLISH_PLURAL_SKIP_LAST = new Set(['parking', 'fitness', 'coworking', 'working']);

function englishPluralPhrase(phrase: string): string | null {
  const words = phrase.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const last = words[words.length - 1];
  if (!last || ENGLISH_PLURAL_SKIP_LAST.has(last) || last.endsWith('s')) {
    return null;
  }
  let pluralLast = `${last}s`;
  if (last.endsWith('y') && last.length > 2 && !/[aeiou]y$/.test(last)) {
    pluralLast = `${last.slice(0, -1)}ies`;
  } else if (/(?:s|x|z|ch|sh)$/.test(last)) {
    pluralLast = `${last}es`;
  }
  return [...words.slice(0, -1), pluralLast].join(' ');
}

function hebrewPluralPhrases(phrase: string): string[] {
  const trimmed = phrase.trim();
  if (!trimmed) {
    return [];
  }
  const out: string[] = [...(HEBREW_TYPE_PLURALS[trimmed] ?? [])];
  if (trimmed.startsWith('חדר ') && !trimmed.startsWith('חדרי ')) {
    out.push(`חדרי ${trimmed.slice('חדר '.length)}`);
  }
  if (trimmed.startsWith('אולם ') && !trimmed.startsWith('אולמות ')) {
    out.push(`אולמות ${trimmed.slice('אולם '.length)}`);
  }
  if (trimmed.startsWith('טרקלין ') && !trimmed.startsWith('טרקליני')) {
    out.push(`טרקליני ${trimmed.slice('טרקלין '.length)}`);
  }
  return out;
}

/** Regular English / Hebrew plurals of a spoken type phrase. */
export function inflectAssetTypePhrase(phrase: string): string[] {
  const trimmed = phrase.trim();
  if (!trimmed) {
    return [];
  }
  if (/[a-z]/i.test(trimmed) && !/[\u0590-\u05ff]/.test(trimmed)) {
    const plural = englishPluralPhrase(trimmed);
    return plural ? [plural] : [];
  }
  return hebrewPluralPhrases(trimmed);
}

type BrowseFilterAssetTypeName = (typeof BROWSE_FILTER_ASSET_TYPE_NAMES)[number];

/** Catalog labels plus synonyms and generated plurals for one browse type. */
export function browseSpokenPhrasesForType(label: BrowseFilterAssetTypeName): string[] {
  const index = BROWSE_FILTER_ASSET_TYPE_NAMES.indexOf(label);
  const seeds = [
    label,
    normalizeAssetTypeName(label),
    BROWSE_FILTER_HEBREW_LABELS[index] ?? '',
    ...(BROWSE_FILTER_TYPE_EXTRA_PHRASES[label] ?? []),
  ];
  const out = new Set<string>();
  for (const seed of seeds) {
    const trimmed = seed.trim();
    if (!trimmed) {
      continue;
    }
    out.add(trimmed);
    out.add(normalizeAssetTypeName(trimmed));
    for (const inflected of inflectAssetTypePhrase(trimmed)) {
      out.add(inflected);
      out.add(normalizeAssetTypeName(inflected));
    }
  }
  return Array.from(out);
}

let spokenPhraseToBrowseType: Map<string, BrowseFilterAssetTypeName> | null = null;

function spokenPhraseToBrowseTypeMap(): Map<string, BrowseFilterAssetTypeName> {
  if (spokenPhraseToBrowseType) {
    return spokenPhraseToBrowseType;
  }
  const map = new Map<string, BrowseFilterAssetTypeName>();
  for (const label of BROWSE_FILTER_ASSET_TYPE_NAMES) {
    for (const phrase of browseSpokenPhrasesForType(label)) {
      const key = normalizeAssetTypeName(phrase);
      if (!key || map.has(key)) {
        continue;
      }
      map.set(key, label);
    }
  }
  spokenPhraseToBrowseType = map;
  return map;
}

/**
 * Map a spoken / backend type string (singular or plural, EN or HE) to the English browse name.
 * Generic “office / משרד” still resolves to Private Office — callers set officeFamily separately.
 */
export function matchBrowseAssetTypeFromSpokenPhrase(phrase: string): BrowseFilterAssetTypeName | null {
  const key = normalizeAssetTypeName(phrase);
  if (!key) {
    return null;
  }
  return spokenPhraseToBrowseTypeMap().get(key) ?? null;
}

/** Hebrew catalog labels for the eleven home / browse asset types (slot order). */
export const BROWSE_FILTER_HEBREW_LABELS = [
  'משרד פרטי',
  'אופן ספייס',
  'חדר ישיבות',
  'חדר כושר',
  'חניון',
  'חדר פודקסטים',
  'אולם הרצאות',
  'חדר טיפול',
  'טרקלין עסקים',
  'כיתה',
  'חדר סדנאות',
] as const;

const BROWSE_FILTER_HEBREW_NORMALIZED = new Set(
  BROWSE_FILTER_HEBREW_LABELS.map((label) => normalizeAssetTypeName(label)),
);

export function normalizeAssetTypeName(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Home “Offices in your vicinity” carousel — current + legacy API labels. */
export const HOME_OFFICE_CAROUSEL_TYPE_NAMES = [
  'Private Office',
  'Open Space',
  'Office',
  'Executive Office',
  'Shared Office',
  'Serviced Office',
  'Hot Desk',
] as const;

/** Home “Gyms with availability” carousel — current + legacy API labels. */
export const HOME_GYM_CAROUSEL_TYPE_NAMES = [
  'Gym',
  'Training Room',
  'Fitness',
  'Fitness Center',
] as const;

const HOME_OFFICE_CAROUSEL_LABELS = new Set(
  HOME_OFFICE_CAROUSEL_TYPE_NAMES.map((name) => normalizeAssetTypeName(name)),
);

const HOME_GYM_CAROUSEL_LABELS = new Set(
  HOME_GYM_CAROUSEL_TYPE_NAMES.map((name) => normalizeAssetTypeName(name)),
);

export function buildHomeCarouselTypeIdSets(types: GeneralInfoPicklistItem[]): {
  officeIds: Set<number>;
  gymIds: Set<number>;
} {
  const officeIds = new Set<number>();
  const gymIds = new Set<number>();
  for (const item of types) {
    const normalized = normalizeAssetTypeName(item.label);
    if (HOME_OFFICE_CAROUSEL_LABELS.has(normalized)) {
      officeIds.add(item.id);
    }
    if (HOME_GYM_CAROUSEL_LABELS.has(normalized)) {
      gymIds.add(item.id);
    }
  }
  return { officeIds, gymIds };
}

export function catalogLabelMatchesHomeOfficeCarousel(label: string): boolean {
  return HOME_OFFICE_CAROUSEL_LABELS.has(normalizeAssetTypeName(label));
}

export function catalogLabelMatchesHomeGymCarousel(label: string): boolean {
  return HOME_GYM_CAROUSEL_LABELS.has(normalizeAssetTypeName(label));
}

/**
 * Spoken “office / משרד” (not “private office”) — Private Office plus lounges,
 * conference rooms, and other office-like catalog types.
 */
export const OFFICE_VOICE_SEARCH_TYPE_NAMES = [
  ...HOME_OFFICE_CAROUSEL_TYPE_NAMES,
  'Business Lounge',
  'Conference Room',
] as const;

const OFFICE_VOICE_SEARCH_LABELS = new Set(
  [
    ...OFFICE_VOICE_SEARCH_TYPE_NAMES,
    'טרקלין עסקים',
    'חדר ישיבות',
    'חדרי ישיבות',
    'משרד פרטי',
    'אופן ספייס',
  ].map((name) => normalizeAssetTypeName(name)),
);

export function catalogLabelMatchesOfficeVoiceSearch(label: string): boolean {
  const normalized = normalizeAssetTypeName(label);
  return OFFICE_VOICE_SEARCH_LABELS.has(normalized) || catalogLabelMatchesHomeOfficeCarousel(label);
}

export function buildOfficeVoiceSearchTypeIdSet(types: GeneralInfoPicklistItem[]): Set<number> {
  const ids = new Set<number>();
  for (const item of types) {
    if (catalogLabelMatchesOfficeVoiceSearch(item.label)) {
      ids.add(item.id);
    }
  }
  return ids;
}

/** Browse strip / list filter slot for {@link BROWSE_FILTER_ASSET_TYPE_NAMES} “Private Office”. */
export const PRIVATE_OFFICE_BROWSE_TYPE_SLOT_INDEX =
  BROWSE_FILTER_ASSET_TYPE_NAMES.indexOf('Private Office');

/** True when the selected browse type is Private Office (home strip / dropdown). */
export function isPrivateOfficeBrowseTypeSelection(
  typeId: number,
  catalogLabelNorm: string | null,
): boolean {
  if (catalogLabelNorm) {
    const normalized = normalizeAssetTypeName(catalogLabelNorm);
    if (normalized === normalizeAssetTypeName('Private Office')) {
      return true;
    }
    if (normalized === normalizeAssetTypeName('Office')) {
      return true;
    }
    if (
      normalized ===
      normalizeAssetTypeName(BROWSE_FILTER_HEBREW_LABELS[PRIVATE_OFFICE_BROWSE_TYPE_SLOT_INDEX])
    ) {
      return true;
    }
  }
  if (typeId === 9000 + PRIVATE_OFFICE_BROWSE_TYPE_SLOT_INDEX) {
    return true;
  }
  return false;
}

/** Browse strip / list filter slot for {@link BROWSE_FILTER_ASSET_TYPE_NAMES} “Gym”. */
export const GYM_BROWSE_TYPE_SLOT_INDEX = BROWSE_FILTER_ASSET_TYPE_NAMES.indexOf('Gym');

/** True when the assets list should use home gym carousel matching (Gym + Training Room + Fitness, etc.). */
export function isHomeGymBrowseTypeSelection(
  typeId: number,
  catalogLabelNorm: string | null,
): boolean {
  if (catalogLabelNorm && catalogLabelMatchesHomeGymCarousel(catalogLabelNorm)) {
    return true;
  }
  if (
    catalogLabelNorm &&
    catalogLabelNorm === normalizeAssetTypeName(BROWSE_FILTER_HEBREW_LABELS[GYM_BROWSE_TYPE_SLOT_INDEX])
  ) {
    return true;
  }
  if (typeId === 9000 + GYM_BROWSE_TYPE_SLOT_INDEX) {
    return true;
  }
  return false;
}

export function resolveHomeBrowseGymType(
  catalog: GeneralInfoPicklistItem[],
): GeneralInfoPicklistItem {
  const types = resolveHomeBrowseAssetTypes(catalog);
  return types[GYM_BROWSE_TYPE_SLOT_INDEX] ?? types[0];
}

export function isBrowseFilterAssetTypeLabel(label: string): boolean {
  const normalized = normalizeAssetTypeName(label);
  return BROWSE_FILTER_ORDER.has(normalized) || BROWSE_FILTER_HEBREW_NORMALIZED.has(normalized);
}

/** Filter + sort browse-menu asset types; unknown types are omitted. */
function browseFilterSortIndex(label: string): number {
  const normalized = normalizeAssetTypeName(label);
  if (BROWSE_FILTER_ORDER.has(normalized)) {
    return BROWSE_FILTER_ORDER.get(normalized) ?? 999;
  }
  const hebrewOrder = BROWSE_FILTER_HEBREW_LABELS.map((value) => normalizeAssetTypeName(value));
  const heIndex = hebrewOrder.indexOf(normalized);
  return heIndex >= 0 ? heIndex : 999;
}

/** Browse UI fallback when `GET /assets/asset-types` is unavailable (nginx 502, etc.). */
export function browseFilterAssetTypesFallback(): GeneralInfoPicklistItem[] {
  return BROWSE_FILTER_ASSET_TYPE_NAMES.map((label, index) => ({
    id: 9000 + index,
    label,
  }));
}

/**
 * Normalized type label for list filtering when the asset-types catalog failed to load.
 * Uses an explicit home-strip label, catalog row, or placeholder slot id (9000 + index).
 */
export function browseFilterLabelForTypeId(
  typeId: number,
  catalog: GeneralInfoPicklistItem[],
  explicitLabel?: string | null,
): string | null {
  if (typeof explicitLabel === 'string' && explicitLabel.trim()) {
    return normalizeAssetTypeName(explicitLabel);
  }
  const fromCatalog = catalog.find((item) => Number(item.id) === typeId)?.label;
  if (typeof fromCatalog === 'string' && fromCatalog.trim()) {
    return normalizeAssetTypeName(fromCatalog);
  }
  if (typeId >= 9000 && typeId < 9000 + BROWSE_FILTER_ASSET_TYPE_NAMES.length) {
    return normalizeAssetTypeName(BROWSE_FILTER_ASSET_TYPE_NAMES[typeId - 9000]);
  }
  return null;
}

export function browseFilterAssetTypes(types: GeneralInfoPicklistItem[]): GeneralInfoPicklistItem[] {
  return types
    .filter((item) => isBrowseFilterAssetTypeLabel(item.label))
    .slice()
    .sort((a, b) => {
      const ai = browseFilterSortIndex(a.label);
      const bi = browseFilterSortIndex(b.label);
      if (ai !== bi) {
        return ai - bi;
      }
      return a.label.localeCompare(b.label);
    });
}

/** Placeholder catalog id for a browse slot when `GET /assets/asset-types` is down. */
export function browseFallbackTypeIdForLabel(label: string): number | null {
  const english = normalizeAssetTypeName(browseCanonicalEnglishLabel(label));
  const index = BROWSE_FILTER_ASSET_TYPE_NAMES.findIndex(
    (name) => normalizeAssetTypeName(name) === english,
  );
  return index >= 0 ? 9000 + index : null;
}

/** Catalog id for a spoken/home type label, or a 9000+ fallback slot id. */
export function matchBrowseFilterTypeId(
  label: string,
  catalog: GeneralInfoPicklistItem[],
): number | null {
  const resolved = matchBrowseAssetTypeFromSpokenPhrase(label) ?? label;
  const english = normalizeAssetTypeName(browseCanonicalEnglishLabel(resolved));
  const match = catalog.find(
    (item) => normalizeAssetTypeName(browseCanonicalEnglishLabel(item.label)) === english,
  );
  if (match && Number.isFinite(Number(match.id))) {
    return Number(match.id);
  }
  return browseFallbackTypeIdForLabel(resolved);
}

/** Map API / Hebrew label to the English browse name (for icons and fallbacks). */
export function browseCanonicalEnglishLabel(label: string): string {
  const normalized = normalizeAssetTypeName(label);
  for (const name of BROWSE_FILTER_ASSET_TYPE_NAMES) {
    if (normalizeAssetTypeName(name) === normalized) {
      return name;
    }
  }
  for (let i = 0; i < BROWSE_FILTER_HEBREW_LABELS.length; i += 1) {
    if (normalizeAssetTypeName(BROWSE_FILTER_HEBREW_LABELS[i]) === normalized) {
      return BROWSE_FILTER_ASSET_TYPE_NAMES[i];
    }
  }
  return label;
}

function catalogRowMatchesBrowseSlot(
  item: GeneralInfoPicklistItem,
  slotIndex: number,
): boolean {
  const normalized = normalizeAssetTypeName(item.label);
  const english = normalizeAssetTypeName(BROWSE_FILTER_ASSET_TYPE_NAMES[slotIndex]);
  const hebrew = normalizeAssetTypeName(BROWSE_FILTER_HEBREW_LABELS[slotIndex]);
  if (normalized === english || normalized === hebrew) {
    return true;
  }
  if (normalized.includes(english) || english.includes(normalized)) {
    return true;
  }
  return false;
}

/**
 * Home asset-type strip: always returns the eleven browse slots, merging catalog ids when available.
 */
export function resolveHomeBrowseAssetTypes(catalog: GeneralInfoPicklistItem[]): GeneralInfoPicklistItem[] {
  const usedIds = new Set<number>();
  const out: GeneralInfoPicklistItem[] = [];

  for (let slot = 0; slot < BROWSE_FILTER_ASSET_TYPE_NAMES.length; slot += 1) {
    const english = BROWSE_FILTER_ASSET_TYPE_NAMES[slot];
    const browseMatch = catalog.find(
      (item) =>
        !usedIds.has(item.id) &&
        isBrowseFilterAssetTypeLabel(item.label) &&
        browseFilterSortIndex(item.label) === slot,
    );
    const fuzzyMatch =
      browseMatch ??
      catalog.find((item) => !usedIds.has(item.id) && catalogRowMatchesBrowseSlot(item, slot));
    if (fuzzyMatch) {
      usedIds.add(fuzzyMatch.id);
      out.push(fuzzyMatch);
      continue;
    }
    out.push({ id: 9000 + slot, label: english });
  }

  return out;
}

/**
 * Desktop banner nav: Offizz.ai category set only (7 types), in Offizz menu order.
 */
export function resolveOffizzSiteNavAssetTypes(
  catalog: GeneralInfoPicklistItem[],
): GeneralInfoPicklistItem[] {
  const all = resolveHomeBrowseAssetTypes(catalog);
  const byEnglish = new Map(
    all.map((item) => [normalizeAssetTypeName(browseCanonicalEnglishLabel(item.label)), item]),
  );
  return OFFIZZ_SITE_NAV_ASSET_TYPE_NAMES.map((name, index) => {
    const match = byEnglish.get(normalizeAssetTypeName(name));
    if (match) {
      return match;
    }
    return { id: 9100 + index, label: name };
  });
}
